import { createSelector, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { createAppAsyncThunk } from "../../../../app/thunkWithTypes";
import PortfolioComposite from "../../../../api/models/portfolioComposite";
import { instanceToPlain, plainToInstance } from 'class-transformer';
import { upsertComposite as apiUpsertComposite } from "../../../../api/client";
import { RootState } from "../../../../app/store";
import User from "../../../../api/models/user";

interface EditCompositeSliceState {
  composite: object | undefined
  parentComposite: object | undefined
  status: 'idle' | 'pending' | 'succeeded' | 'failed'
}

const initialState: EditCompositeSliceState = {
  composite: undefined,
  parentComposite: undefined,
  status: 'idle'
}

export const upsertComposite = createAppAsyncThunk('editComposite/upsert', async (data: { accessToken: string, composite: PortfolioComposite, user: User }) => {
  const { accessToken, composite, user } = data;
  if (user && composite) {
    const id = await apiUpsertComposite(accessToken, user.id, composite);
    if (id) {
      composite.id = id;
      return instanceToPlain(composite);
    }
  } else {
    return undefined;
  }
}, {
  condition(arg, thunkApi) {
    const status = selectEditCompositeStatus(thunkApi.getState())
    if (status !== 'idle') {
      return false;
    }
  }
})

const editCompositeSlice = createSlice({
  name: 'editComposite',
  initialState,
  reducers: {
    setCompositeEditing(state, action: PayloadAction<{ composite: object | undefined, parent: object | undefined }>) {
      state.composite = action.payload.composite;
      state.parentComposite = action.payload.parent;
      state.status = 'idle';
    },
  },
  extraReducers: builder => {
    builder
      .addCase(upsertComposite.pending, (state, action) => {
        state.status = 'pending'
      })
      .addCase(upsertComposite.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.composite = undefined;
      })
      .addCase(upsertComposite.rejected, (state, action) => {
        state.status = 'failed'
      })
  }
})

// Export selectors
export const selectCompositeEditing = createSelector([(state: RootState) => state.editComposite.composite], (compositeEditing) => {
  if (compositeEditing) {
    return plainToInstance(PortfolioComposite, compositeEditing);
  } else {
    return undefined;
  }
})
export const selectCompositeEditingParent = createSelector([(state: RootState) => state.editComposite.parentComposite], (parent) => {
  if (parent) {
    return plainToInstance(PortfolioComposite, parent);
  } else {
    return undefined;
  }
})
export const selectIsAnyCompositeEditing = createSelector([(state: RootState) => state.editComposite.composite], (compositeEditing) => {
  return compositeEditing ? true : false;
})
export const selectEditCompositeStatus = (state: RootState) => state.editComposite.status;

// Export the auto-generated action creator with the same name
export const { setCompositeEditing } = editCompositeSlice.actions;

// Export the generated reducer function
export default editCompositeSlice.reducer;